import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import classNames from 'classnames';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';

import { LessonList } from './LessonList';

export const CHAPTER_FRAGMENT = graphql(/* GraphQL */ `
  fragment EmbaCourseChapter on CourseChapter {
    id
    name
    slug
    isCurrentChapter
    lessons {
      id
      ...EmbaCourseLesson
    }
  }
`);

export type AccordionProps = {
  chapter: FragmentType<typeof CHAPTER_FRAGMENT>;
  onClick: () => void;
  embaUserAttendanceSlug: string;
  type?: 'sidebar' | 'lessons';
};

export const Accordion: React.FC<AccordionProps> = ({
  chapter,
  onClick,
  embaUserAttendanceSlug,
  type = 'sidebar',
}) => {
  const chapterData = useFragment(CHAPTER_FRAGMENT, chapter);

  const [isOpen, setIsOpen] = useState(chapterData.isCurrentChapter);
  const [maxHeight, setMaxHeight] = useState<string | number>(0);
  const contentRef = useRef<HTMLUListElement>(null);

  const toggleAccordion = useCallback(() => {
    setIsOpen((prevIsOpen: boolean) => !prevIsOpen);
  }, []);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setMaxHeight(contentRef.current.scrollHeight);
    } else {
      setMaxHeight(0);
    }
  }, [isOpen]);

  return (
    <li
      key={chapterData.name}
      className={classNames(styles.accordion, {
        [styles.open]: isOpen,
        [styles.sidebar]: type === 'sidebar',
        [styles.lessons]: type === 'lessons',
      })}
    >
      <h4
        className={classNames(styles.chapterName, {
          primaryShadow: type === 'sidebar',
          [styles.current]: chapterData.isCurrentChapter,
        })}
        onClick={toggleAccordion}
      >
        <b>{chapterData.name}</b>
        <span>&nbsp;</span>
      </h4>
      <ul
        ref={contentRef}
        className={classNames(styles.lessonList, { hidden: !isOpen })}
        style={{ maxHeight }}
      >
        {chapterData.lessons.map((lesson) => (
          <LessonList
            key={lesson.id}
            embaUserAttendanceSlug={embaUserAttendanceSlug}
            onClick={onClick}
            lesson={lesson}
            type={type}
          />
        ))}
      </ul>
    </li>
  );
};
